import React from "react";

interface ExitModalContextProps {
  isExitModalVisible: boolean;
  getIsExternalLink: (url: string) => boolean;
  openExitModal: (open: boolean) => void;
  openExitModalForLink: (url: string, reason?: string) => void;
  exitLink: string;
  exitReason: string;
}
const ExitModalContext = React.createContext<ExitModalContextProps>({
  isExitModalVisible: false,
  getIsExternalLink: () => false,
  openExitModal: () => {},
  openExitModalForLink: () => {},
  exitLink: "",
  exitReason: ""
});
export const ExitModalProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isExitModalVisible, setExitModalVisible] = React.useState(false);
  const [exitLink, setExitLink] = React.useState("");
  const [exitReason, setExitReason] = React.useState("");
  const openExitModal = React.useCallback(
    (open: boolean) => setExitModalVisible(open),
    [setExitModalVisible]
  );
  const openExitModalForLink = React.useCallback(
    (url: string, reason?: string) => {
      setExitLink(url);
      setExitReason(reason || "");
      setExitModalVisible(true);
    },
    [setExitLink, setExitReason, setExitModalVisible]
  );
  const getIsExternalLink = React.useCallback((url: string) => {
    const urlLower = url.toLowerCase();
    if (
      urlLower.startsWith("/") ||
      urlLower.startsWith("mailto:") ||
      urlLower.startsWith("tel:") ||
      urlLower.startsWith("#")
    ) {
      return false;
    }
    try {
      const urlObject = new URL(url);
      if (isInternalDomain(urlObject.hostname.toLowerCase())) {
        return false;
      } else {
        setExitLink(url);
        return true;
      }
    } catch {
      return false;
    }
  }, []);
  const value = React.useMemo(
    () => ({
      isExitModalVisible,
      openExitModal,
      openExitModalForLink,
      getIsExternalLink,
      exitLink,
      exitReason
    }),
    [
      isExitModalVisible,
      openExitModal,
      openExitModalForLink,
      getIsExternalLink,
      exitLink,
      exitReason
    ]
  );
  return <ExitModalContext.Provider value={value}>{children}</ExitModalContext.Provider>;
};
export const useExitModal = () => React.useContext(ExitModalContext);

const isInternalDomain = (domain: string) => {
  const internalDomains = [
    "disease.education",
    "modernacovid19global.com",
    "modernadirect.com",
    "modernatx.com",
    "report.moderna.convergehealthsafety.com"
  ];
  const escapeRegex = (string: string) => string.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
  const domainPattern = internalDomains.map(escapeRegex).join("|");
  const internalDomainRegex = new RegExp(`${domainPattern}`, "i");
  return internalDomainRegex.test(domain);
};
